/**
 * <free-shipping-line> — the free shipping caption under the price on product pages.
 *
 * The theme's default caption ("Shipping calculated at checkout") is rendered
 * directly before this element. Whenever this line is actually visible, that
 * caption must be hidden, or the shopper reads two shipping messages at once.
 *
 * In the published theme the stylesheet does that at paint time. This script
 * exists for an A/B test: the line is then wrapped in a `[data-hi-exp]` gate
 * that stays hidden for the control group and is revealed for the test group
 * once the visitor is bucketed, so whether the line is visible is only known at
 * runtime. For the control group the line never becomes visible, so the default
 * caption is left exactly as it is.
 */
if (!customElements.get('free-shipping-line')) {
  customElements.define(
    'free-shipping-line',
    class FreeShippingLine extends HTMLElement {
      connectedCallback() {
        const gate = this.closest('[data-hi-exp]');
        const previous = (gate || this).previousElementSibling;
        this.defaultCaption =
          previous && previous.classList.contains('product__tax') && !previous.classList.contains('free-shipping-line')
            ? previous
            : null;
        if (!this.defaultCaption) return;

        this.sync();
        if (gate && !this.observer) {
          // The test group is revealed by a class on <body>. Follow it so the
          // default caption disappears in the same frame the line appears.
          this.observer = new MutationObserver(() => this.sync());
          this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.defaultCaption) this.defaultCaption.classList.remove('product__tax--replaced');
      }

      sync() {
        // An element inside a hidden gate has no box; a visible one has.
        const visible = this.getClientRects().length > 0;
        this.defaultCaption.classList.toggle('product__tax--replaced', visible);
      }
    }
  );
}
